import { NavLink, Outlet, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import {
  LayoutDashboard,
  Boxes,
  Calculator,
  Upload,
  SlidersHorizontal,
  ChefHat,
  BookOpen,
  Building2,
  ShieldCheck,
  LogOut,
  FileText,
  SlidersHorizontal as SlidersIcon,
} from "lucide-react";

const nav = [
  { to: "/", label: "Přehled", icon: LayoutDashboard, testid: "nav-dashboard" },
  { to: "/kalkulace", label: "Kalkulace", icon: Calculator, testid: "nav-calculations" },
  { to: "/nabidky", label: "Nabídky", icon: FileText, testid: "nav-offers" },
  { to: "/receptury", label: "Receptury", icon: BookOpen, testid: "nav-recipes" },
  { to: "/odberatele", label: "Odběratelé", icon: Building2, testid: "nav-customers" },
  { to: "/suroviny", label: "Suroviny", icon: Boxes, testid: "nav-materials" },
];

const adminNav = [
  { to: "/nastaveni", label: "Nastavení výpočtů", icon: SlidersHorizontal, testid: "nav-settings" },
  { to: "/import", label: "Import dat", icon: Upload, testid: "nav-import" },
  { to: "/nastaveni-kalkulaci", label: "Nastavení kalkulací", icon: Calculator, testid: "nav-calc-settings", adminOnly: true },
  { to: "/nabidky-nastaveni", label: "Nastavení nabídky", icon: SlidersIcon, testid: "nav-offer-settings", adminOnly: true },
  { to: "/admin", label: "Správa uživatelů", icon: ShieldCheck, testid: "nav-admin", adminOnly: true },
];

export default function Layout() {
  const loc = useLocation();
  const { user, logout } = useAuth();
  const adminItems = adminNav.filter((n) => !n.adminOnly || user?.role === "admin");
  const renderLink = (n) => {
    const active = n.to === "/" ? loc.pathname === "/" : loc.pathname.startsWith(n.to);
    const Icon = n.icon;
    return (
      <NavLink
        key={n.to}
        to={n.to}
        data-testid={n.testid}
        className={`nav-link flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium ${
          active
            ? "bg-primary text-primary-foreground"
            : "text-foreground/70 hover:bg-muted hover:text-foreground"
        }`}
      >
        <Icon className="h-[18px] w-[18px]" strokeWidth={2} />
        {n.label}
      </NavLink>
    );
  };
  return (
    <div className="min-h-screen flex bg-background relative z-10">
      <aside className="w-64 shrink-0 border-r border-border bg-white flex flex-col fixed h-screen">
        <div className="px-6 py-7 border-b border-border">
          <div className="flex items-center gap-2.5">
            <div className="h-9 w-9 rounded-lg bg-primary flex items-center justify-center">
              <ChefHat className="h-5 w-5 text-primary-foreground" strokeWidth={2} />
            </div>
            <div>
              <div className="font-display font-bold text-[15px] leading-tight tracking-tight">Kalkulace</div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Zdravé stravování</div>
            </div>
          </div>
        </div>
        <nav className="flex-1 px-3 py-5 space-y-1 overflow-y-auto">
          {nav.map(renderLink)}
          {adminItems.length > 0 && (
            <div className="pt-5 pb-1.5 px-3 text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-bold" data-testid="nav-admin-section">
              Administrace
            </div>
          )}
          {adminItems.map(renderLink)}
        </nav>
        <div className="px-3 py-4 border-t border-border">
          <div className="flex items-center gap-3 px-3 py-2">
            <div className="h-8 w-8 rounded-full bg-primary/10 text-primary flex items-center justify-center font-display font-bold text-sm shrink-0">
              {(user?.name || "?").charAt(0).toUpperCase()}
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-sm font-medium truncate" data-testid="sidebar-user-name">{user?.name}</div>
              <div className="text-[11px] text-muted-foreground truncate">{user?.role === "admin" ? "Administrátor" : "Uživatel"}</div>
            </div>
            <button onClick={logout} data-testid="logout-btn" title="Odhlásit se"
              className="text-muted-foreground hover:text-destructive p-1.5 rounded-md hover:bg-muted">
              <LogOut className="h-4 w-4" />
            </button>
          </div>
        </div>
      </aside>

      <main className="flex-1 ml-64 min-h-screen">
        <Outlet />
      </main>
    </div>
  );
}
